import { canConnect } from '@/logic/node-rules';
import type { AppEdge, AppNode, NodeKind } from '@/types';

/**
 * 驗證結果
 * - `ok: true`:通過
 * - `ok: false`:未通過,附帶錯誤代碼與訊息(訊息通常為 i18n 前的原文)
 */
export type ValidationResult = { ok: true } | { ok: false; code: string; message: string };

/**
 * RulesEngine 介面
 * -----------------------------------------------------------------------------
 * - `validateEdge`:單條邊驗證,拖拉連線時即時呼叫
 * - `validateGraph`:整張圖驗證,儲存 / 提交前最後防線
 * - `loadSchema`:從後端拉取最新規則版本(client-side 回固定字串)
 *
 * 前端規則驗證只做「即時 UX」,後端必須二次驗證(權威)。
 *
 * R41.6 起為 by-design client-side service(`createRulesEngine` 直接轉呼
 * `logic/node-rules.canConnect` 純函式);砍 mock layer 後 implementation 收進這檔。
 */
export interface RulesEngine {
  validateEdge(input: { sourceKind: NodeKind; targetKind: NodeKind }): ValidationResult;
  validateGraph(input: { nodes: AppNode[]; edges: AppEdge[] }): ValidationResult;
  loadSchema(version?: string): Promise<{ schemaVersion: string }>;
}

/**
 * Client-side RulesEngine impl。轉呼 `logic/node-rules.canConnect` 純函式。
 * 未來真後端會下發 JSON schema → 改寫這個 factory 即可,caller 不必動。
 */
export function createRulesEngine(): RulesEngine {
  return {
    validateEdge({ sourceKind, targetKind }) {
      return canConnect(sourceKind, targetKind);
    },

    validateGraph({ edges, nodes }) {
      const kindOf = new Map(nodes.map((n) => [n.id, n.type]));
      for (const e of edges) {
        const s = kindOf.get(e.source);
        const t = kindOf.get(e.target);
        if (!s || !t) continue;
        const result = canConnect(s, t);
        if (!result.ok) return result;
      }
      return { ok: true } satisfies ValidationResult;
    },

    async loadSchema(version = 'client-1') {
      return { schemaVersion: version };
    },
  };
}
